import type { Task } from '../api.js'
import type { SourceProject } from './taskSource.js'
import { isOpen, notArchived } from './activeOnly.js'

/**
 * Gantt row and bar model (pure). Rows are the visible tasks in tree order; in
 * a portfolio each project's tree sits under a project header row (which can
 * be collapsed). Bars: leaves use their own dates, parents span their subtree.
 */
export interface Bar { task: Task; start: string; end: string; summary: boolean }   // end inclusive
export type GRow = { kind: 'project'; project: SourceProject } | { kind: 'task'; task: Task }
export interface Span { start: string; end: string }


/**
 * Group tasks by project in `projects` order, each under a header row (unless
 * `headers` is false — the single-project Gantt), skipping the trees of
 * collapsed projects and, when `activeOnly` is set, closed tasks with nothing open under them,
 * and archived tasks unless `showArchived`. `rowIndex` maps
 * a task id to its row number (only visible tasks), which is what the SVG uses
 * for y. Tasks of a project not in `projects` keep their order at the end.
 */
export function groupRows(tasks: Task[], projects: SourceProject[], collapsed: Set<string>, activeOnly: boolean, headers = true, showArchived = false): { rows: GRow[]; rowIndex: Map<string, number> } {
  const byProject = new Map<string, Task[]>()
  for (const t of tasks) { const l = byProject.get(t.projectId); if (l) l.push(t); else byProject.set(t.projectId, [t]) }
  const rows: GRow[] = []
  const rowIndex = new Map<string, number>()
  const pushTasks = (list: Task[]) => { for (const t of list) if (isOpen(t, activeOnly) && notArchived(t, showArchived)) { rowIndex.set(t.id, rows.length); rows.push({ kind: 'task', task: t }) } }
  for (const p of projects) {
    const list = byProject.get(p.id) ?? []
    byProject.delete(p.id)
    if (headers) rows.push({ kind: 'project', project: p })
    if (!headers || !collapsed.has(p.id)) pushTasks(list)
  }
  for (const list of byProject.values()) pushTasks(list)
  return { rows, rowIndex }
}

const own = (t: Task): [string, string] | null => {
  if (t.startsOn && t.dueOn) return [t.startsOn, t.dueOn]
  if (t.startsOn) return [t.startsOn, t.startsOn]
  if (t.dueOn) return [t.dueOn, t.dueOn]
  return null
}

/**
 * Bars for every dated task: a leaf's own span, a parent's subtree span
 * (including its own dates). One pass: each task's span is pushed up its
 * `path` ancestors, so the cost is O(n · depth) rather than n² filters.
 */
export function computeBars(tasks: Task[]): Map<string, Bar> {
  const acc = new Map<string, Span>()
  for (const t of tasks) {
    const o = own(t)
    if (!o) continue
    for (const id of t.path) {
      const s = acc.get(id)
      if (!s) acc.set(id, { start: o[0], end: o[1] })
      else { if (o[0] < s.start) s.start = o[0]; if (o[1] > s.end) s.end = o[1] }
    }
    if (!t.path.includes(t.id)) {   // defensive: a path that omits the task itself
      const s = acc.get(t.id)
      if (!s) acc.set(t.id, { start: o[0], end: o[1] })
      else { if (o[0] < s.start) s.start = o[0]; if (o[1] > s.end) s.end = o[1] }
    }
  }
  const m = new Map<string, Bar>()
  for (const t of tasks) {
    if (t.childCount > 0) {
      const s = acc.get(t.id)
      if (s) m.set(t.id, { task: t, start: s.start, end: s.end, summary: true })
    } else {
      const o = own(t)
      if (o) m.set(t.id, { task: t, start: o[0], end: o[1], summary: false })
    }
  }
  return m
}

/** Each project's earliest start and latest end over its bars (for the portfolio summary bar). */
export function projectSpans(bars: Iterable<Bar>, span: (b: Bar) => [string, string] = (b) => [b.start, b.end]): Map<string, Span> {
  const m = new Map<string, Span>()
  for (const b of bars) {
    const [start, end] = span(b)
    const s = m.get(b.task.projectId)
    if (!s) m.set(b.task.projectId, { start, end })
    else { if (start < s.start) s.start = start; if (end > s.end) s.end = end }
  }
  return m
}
